import { translateIdentity, type StaticTextTranslator } from "../../utils";
import styles from "./ProgressBar.module.css";

export interface ProgressBarProps {
  /** 1-based; absent when progress should not be surfaced. */
  currentQuestionNumber?: number;
  /** Total counted questions for the interview. */
  totalQuestions?: number;
  /** Optional translation function for the "Question X of Y" label. */
  translate?: StaticTextTranslator;
}

/**
 * AS-1564 — candidate-facing progress indicator. Renders inside the Header
 * as a second row beneath the company / interview title.
 *
 * Visual + behavioral spec sourced from the Claude Design handoff bundle
 * (progress-bar/project/index.html). Hidden when either field is
 * missing/invalid — the orchestrator and screener-API decide *when* to emit;
 * the component does not second-guess them.
 *
 * Fill formula `((current - 1) / total) * 100` is the canonical exported
 * mapping: Q1 starts at 0%, the last counted question shows `(total - 1)/total`,
 * and only `current = total + 1` (emitted at CLOSING/COMPLETED) drives the
 * bar to 100%. See tech brief §10.3.
 */
export function ProgressBar({
  currentQuestionNumber,
  totalQuestions,
  translate = translateIdentity,
}: ProgressBarProps) {
  if (
    currentQuestionNumber == null ||
    totalQuestions == null ||
    !Number.isFinite(currentQuestionNumber) ||
    !Number.isFinite(totalQuestions) ||
    // `currentQuestionNumber` is 1-based — a 0 (or negative) value is
    // graph-state drift, not a real position. Without this guard a leaked
    // 0 would render "Question 0 of Y", which is worse than hiding the bar.
    currentQuestionNumber < 1 ||
    totalQuestions <= 0
  ) {
    return null;
  }

  // Label clamps so the candidate never sees "Question 9 of 8" at completion;
  // the fill formula handles 100% via current = total + 1 instead.
  const labelCurrent = Math.min(currentQuestionNumber, totalQuestions);
  const fillPercent = Math.max(
    0,
    Math.min(100, ((currentQuestionNumber - 1) / totalQuestions) * 100),
  );
  // The label is translated by an LLM (apiService.translateTexts) that
  // localizes the *words inside the placeholders* — e.g. "{current}" becomes
  // "{actual}" in Spanish — so we can't match by name. Substitute the brace
  // tokens by position instead. The translator can't fill them (it has no
  // numbers), so for an "N of M" phrase two tokens normally survive in order.
  // (AS-1901 — fixes "Pregunta {actual} de 6" not updating.)
  const values = [String(labelCurrent), String(totalQuestions)];
  const translated = translate("Question {current} of {total}");
  // Guard against unbounded LLM output: if the translation didn't come back
  // with exactly two brace tokens (one dropped or an extra introduced),
  // positional substitution would leave a literal "{…}" on screen. Fall back
  // to the English template, which always has exactly two — a fully numbered
  // English label beats a half-interpolated localized one.
  const template =
    (translated.match(/\{[^}]+\}/g) ?? []).length === 2
      ? translated
      : "Question {current} of {total}";
  let valueIndex = 0;
  const label = template.replace(
    /\{[^}]+\}/g,
    () => values[valueIndex++] ?? "",
  );

  return (
    <div
      className={styles.progressRow}
      role="progressbar"
      aria-label={label}
      aria-valuenow={labelCurrent}
      aria-valuemin={0}
      aria-valuemax={totalQuestions}
      data-testid="progress-bar"
    >
      <span className={styles.label} data-testid="progress-bar-label">
        {label}
      </span>
      <div className={styles.track} aria-hidden="true">
        <div
          className={styles.fill}
          data-testid="progress-bar-fill"
          style={{ width: `${fillPercent}%` }}
        />
      </div>
    </div>
  );
}
